import {
  ANONYMOUS_UI_USAGE_EVENT_NAMES,
  AnonymousUiUsageEventSchema,
  UiUsageEventSchema,
  type AnonymousUiUsageEventFields,
  type AnonymousUiUsageEventName,
  type TrackedUiEventFields,
  type TrackedUiEventName,
  type UiUsageEventFields,
  type UiUsageEventName,
} from '@packages/types';
import { anonymousCommonFields, currentUiUsageSink, nextCommonFields } from './ui-usage.runtime';

function isAnonymousName(name: TrackedUiEventName): name is AnonymousUiUsageEventName {
  return ANONYMOUS_UI_USAGE_EVENT_NAMES.some((anonymousName) => anonymousName === name);
}

/**
 * Records one UI usage event on the channel its registry entry declares: an anonymous name goes
 * out at once without identity, an account name takes the queue. Stamps the common fields,
 * validates against that channel's wire union and hands the event to the installed sink. An
 * event the schema refuses is never sent: the sink reports it (dev log, production Sentry, a
 * thrown error in tests).
 */
export function track<N extends UiUsageEventName>(name: N, fields: UiUsageEventFields<N>): void;
export function track<N extends AnonymousUiUsageEventName>(name: N, fields: AnonymousUiUsageEventFields<N>): void;
/** Wide form for a call site generic over either channel (the hooks). */
export function track<N extends TrackedUiEventName>(name: N, fields: TrackedUiEventFields<N>): void;
export function track(name: TrackedUiEventName, fields: object): void {
  const sink = currentUiUsageSink();
  // Parsing through the registry types the event without a cast, and keeps one malformed
  // event from getting a whole batch refused.
  if (isAnonymousName(name)) {
    const parsed = AnonymousUiUsageEventSchema.safeParse({ name, payload: { ...fields, ...anonymousCommonFields() } });
    if (parsed.success) sink.acceptAnonymous(parsed.data);
    else sink.reject(name, parsed.error.issues.map((issue) => issue.path.join('.')));
    return;
  }
  const parsed = UiUsageEventSchema.safeParse({ name, payload: { ...fields, ...nextCommonFields() } });
  if (parsed.success) sink.accept(parsed.data);
  else sink.reject(name, parsed.error.issues.map((issue) => issue.path.join('.')));
}
